/**
 * useAttachmentUpload — shared attachment upload logic.
 * Used by AttachmentPanel (Files tab) and RuntimeFormRenderer (form-wide DnD).
 */
import { useCallback, useState } from 'react'
import apiClient from '@/utils/api'

export interface AttachmentOwner {
  connectionId: string
  ownerType: string
  ownerName: string
  ownerId: string
}

export function attachmentOwnerQuery(owner: AttachmentOwner): string {
  return (
    `connection_id=${encodeURIComponent(owner.connectionId)}` +
    `&owner_type=${encodeURIComponent(owner.ownerType)}` +
    `&owner_name=${encodeURIComponent(owner.ownerName)}` +
    `&owner_id=${encodeURIComponent(owner.ownerId)}`
  )
}

export function useAttachmentUpload(owner: AttachmentOwner) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const uploadFiles = useCallback(
    async (files: FileList | File[]): Promise<number> => {
      setUploading(true)
      setError(null)
      let uploaded = 0
      for (const file of Array.from(files)) {
        try {
          const formData = new FormData()
          formData.append('file', file)
          await apiClient.post(
            `/v1/attachments/upload?${attachmentOwnerQuery(owner)}&auto_ocr=true`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } },
          )
          uploaded += 1
        } catch (e: unknown) {
          setError(e instanceof Error ? e.message : 'Upload failed')
        }
      }
      setUploading(false)
      return uploaded
    },
    [owner.connectionId, owner.ownerType, owner.ownerName, owner.ownerId],
  )

  const clearError = useCallback(() => setError(null), [])

  return { uploadFiles, uploading, error, clearError }
}
